import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/ProjectManagerAdvanced.css';

/* ───────────────────────────────── Types ───────────────────────────────── */

interface TeamMember {
  id: string;
  name: string;
  role: string;
  avatar: string;
  status: 'online' | 'busy' | 'offline';
}

interface Task {
  id: string;
  title: string;
  description: string;
  assignee: TeamMember;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'todo' | 'in-progress' | 'review' | 'done';
  dueDate: Date;
  estimatedHours: number;
  actualHours: number;
  tags: string[];
  progress: number; // 0-100
  // optional startDate for Gantt; fallback is project.startDate
  startDate?: Date;
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed';
  progress: number; // 0-100
  budget: number;
  spent: number;
  startDate: Date;
  endDate: Date;
  tasks: Task[];
  team: TeamMember[];
}

const STATUSES = ['todo', 'in-progress', 'review', 'done'] as const;
type StatusUnion = typeof STATUSES[number];

const PRIORITIES: Task['priority'][] = ['low', 'medium', 'high', 'urgent'];

/* ─────────────────────────────── Utilities ─────────────────────────────── */

const fmtMoney = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const getPriorityColor = (priority: Task['priority']) => {
  switch (priority) {
    case 'urgent': return '#EF4444';
    case 'high': return '#F59E0B';
    case 'medium': return '#10B981';
    case 'low': return '#6B7280';
    default: return '#6B7280';
  }
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/* ─────────────────────────────── Component ─────────────────────────────── */

const ProjectManagerAdvanced: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const activeProject = useMemo(
    () => projects.find(p => p.id === activeProjectId) || null,
    [projects, activeProjectId]
  );

  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'dashboard' | 'kanban' | 'gantt' | 'team'>(
    (localStorage.getItem('pm:view') as any) || 'dashboard'
  );

  // time tracking on/off per task
  const [timeTracking, setTimeTracking] = useState<Record<string, boolean>>({});
  // ticking every second to increment actualHours for tracked tasks
  const tickingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [notifications, setNotifications] = useState(12);

  // Filters
  const [search, setSearch] = useState('');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<Task['priority'] | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<StatusUnion | 'all'>('all');

  // Quick add
  const [newTitle, setNewTitle] = useState('');
  const [newAssignee, setNewAssignee] = useState<string>('unassigned');
  const [newPriority, setNewPriority] = useState<Task['priority']>('medium');

  /* ─────────────────────── Load / Persist LocalStorage ───────────────────── */

  // One-time bootstrap from localStorage or mock
  useEffect(() => {
    const raw = localStorage.getItem('pm:projects');
    if (raw) {
      const parsed = JSON.parse(raw) as Project[];
      // revive dates
      parsed.forEach(p => {
        p.startDate = new Date(p.startDate);
        p.endDate = new Date(p.endDate);
        p.tasks.forEach(t => {
          t.dueDate = new Date(t.dueDate);
          if (t.startDate) t.startDate = new Date(t.startDate);
        });
      });
      setProjects(parsed);
      setActiveProjectId(localStorage.getItem('pm:activeProjectId') || parsed[0]?.id || null);
      setLoading(false);
      return;
    }

    // No cache? Load mock then persist
    setLoading(true);
    const timer = setTimeout(() => {
      const mockTeam: TeamMember[] = [
        { id: '1', name: 'Sarah Wilson', role: 'Project Manager', avatar: '👩‍💼', status: 'online' },
        { id: '2', name: 'Mike Chen', role: 'Frontend Developer', avatar: '👨‍💻', status: 'online' },
        { id: '3', name: 'Lisa Rodriguez', role: 'UX Designer', avatar: '👩‍🎨', status: 'busy' },
        { id: '4', name: 'David Kim', role: 'Backend Developer', avatar: '👨‍💻', status: 'online' },
        { id: '5', name: 'Emma Thompson', role: 'QA Engineer', avatar: '👩‍🔬', status: 'offline' }
      ];

      const now = Date.now();

      const mockTasks: Task[] = [
        {
          id: '1',
          title: 'Implement user authentication system',
          description: 'Build secure login and registration with JWT tokens',
          assignee: mockTeam[3],
          priority: 'high',
          status: 'in-progress',
          startDate: new Date(now - 3 * 24 * 60 * 60 * 1000),
          dueDate: new Date(now + 5 * 24 * 60 * 60 * 1000),
          estimatedHours: 24,
          actualHours: 16,
          tags: ['backend', 'security', 'api'],
          progress: 65
        },
        {
          id: '2',
          title: 'Design dashboard interface',
          description: 'Create wireframes and high-fidelity mockups for admin dashboard',
          assignee: mockTeam[2],
          priority: 'medium',
          status: 'review',
          startDate: new Date(now - 6 * 24 * 60 * 60 * 1000),
          dueDate: new Date(now + 3 * 24 * 60 * 60 * 1000),
          estimatedHours: 16,
          actualHours: 14,
          tags: ['design', 'ui', 'dashboard'],
          progress: 85
        },
        {
          id: '3',
          title: 'Set up CI/CD pipeline',
          description: 'Configure automated testing and deployment workflow',
          assignee: mockTeam[1],
          priority: 'urgent',
          status: 'todo',
          startDate: new Date(now - 1 * 24 * 60 * 60 * 1000),
          dueDate: new Date(now + 2 * 24 * 60 * 60 * 1000),
          estimatedHours: 12,
          actualHours: 0,
          tags: ['devops', 'automation', 'deployment'],
          progress: 0
        },
        {
          id: '4',
          title: 'Write API documentation',
          description: 'Document all endpoints with examples and response formats',
          assignee: mockTeam[3],
          priority: 'low',
          status: 'done',
          startDate: new Date(now - 9 * 24 * 60 * 60 * 1000),
          dueDate: new Date(now - 2 * 24 * 60 * 60 * 1000),
          estimatedHours: 8,
          actualHours: 6,
          tags: ['documentation', 'api'],
          progress: 100
        }
      ];

      const mockProject: Project = {
        id: '1',
        name: 'E-Commerce Platform Redesign',
        description: 'Complete overhaul of the existing e-commerce platform with modern UI and enhanced performance',
        status: 'active',
        progress: 64,
        budget: 150000,
        spent: 89500,
        startDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date(now + 45 * 24 * 60 * 60 * 1000),
        tasks: mockTasks,
        team: mockTeam
      };

      const seeded = [mockProject];
      setProjects(seeded);
      setActiveProjectId('1');
      setLoading(false);
      localStorage.setItem('pm:projects', JSON.stringify(seeded));
      localStorage.setItem('pm:activeProjectId', '1');
    }, 400);

    return () => clearTimeout(timer);
  }, []);

  // Persist on project changes
  useEffect(() => {
    if (projects.length) {
      localStorage.setItem('pm:projects', JSON.stringify(projects));
    }
  }, [projects]);

  useEffect(() => {
    if (activeProjectId) localStorage.setItem('pm:activeProjectId', activeProjectId);
  }, [activeProjectId]);

  useEffect(() => {
    localStorage.setItem('pm:view', activeView);
  }, [activeView]);

  /* ───────────────────────────── Real-time loop ─────────────────────────── */

  useEffect(() => {
    // notifications & time tracking ticking each 1s for better UX
    if (tickingRef.current) clearInterval(tickingRef.current);
    tickingRef.current = setInterval(() => {
      if (Math.random() > 0.97) setNotifications(prev => prev + 1);

      setProjects(prevProjects => {
        if (!activeProject) return prevProjects;
        return prevProjects.map(p => {
          if (p.id !== activeProject.id) return p;
          const tasks = p.tasks.map(t => {
            if (timeTracking[t.id] && t.status === 'in-progress') {
              const add = 1 / 3600; // 1 second in hours
              return {
                ...t,
                actualHours: +(t.actualHours + add).toFixed(2),
                progress: clamp(t.progress + 0.05, 0, 100)
              };
            }
            return t;
          });
          const progress = Math.round(
            tasks.reduce((acc, t) => acc + t.progress, 0) / Math.max(1, tasks.length)
          );
          return { ...p, tasks, progress };
        });
      });
    }, 1000);

    return () => {
      if (tickingRef.current) clearInterval(tickingRef.current);
    };
  }, [timeTracking, activeProject]);

  /* ───────────────────────────── Event Handlers ─────────────────────────── */

  const handleProjectChange = (projectId: string) => {
    setActiveProjectId(projectId || null);
  };

  const setProject = (updater: (p: Project) => Project) => {
    if (!activeProject) return;
    setProjects(prev => prev.map(p => (p.id === activeProject.id ? updater(p) : p)));
  };

  const handleTaskStatusChange = (taskId: string, newStatus: Task['status']) => {
    setProject(p => ({
      ...p,
      tasks: p.tasks.map(t => (t.id === taskId ? { ...t, status: newStatus } : t))
    }));
  };

  const handleTaskPriorityChange = (taskId: string, newPriority: Task['priority']) => {
    setProject(p => ({
      ...p,
      tasks: p.tasks.map(t => (t.id === taskId ? { ...t, priority: newPriority } : t))
    }));
  };

  const toggleTime = (taskId: string) =>
    setTimeTracking(prev => ({ ...prev, [taskId]: !prev[taskId] }));

  // Quick add task (minimal)
  const addTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeProject || !newTitle.trim()) return;
    const assignee =
      activeProject.team.find(m => m.id === newAssignee) || activeProject.team[0];
    const now = Date.now();
    const t: Task = {
      id: (Math.random() * 1e9).toFixed(0),
      title: newTitle.trim(),
      description: '—',
      assignee,
      priority: newPriority,
      status: 'todo',
      startDate: new Date(now),
      dueDate: new Date(now + 7 * 24 * 60 * 60 * 1000),
      estimatedHours: 8,
      actualHours: 0,
      tags: [],
      progress: 0
    };
    setProject(p => ({ ...p, tasks: [t, ...p.tasks] }));
    setNewTitle('');
    setNewAssignee('unassigned');
    setNewPriority('medium');
  };

  /* ────────────────────────────── DnD Kanban ────────────────────────────── */

  const [dragTaskId, setDragTaskId] = useState<string | null>(null);

  const onDragStart = (taskId: string) => setDragTaskId(taskId);
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDropTo = (status: StatusUnion) => {
    if (!dragTaskId || !activeProject) return;
    handleTaskStatusChange(dragTaskId, status);
    setDragTaskId(null);
  };

  /* ───────────────────────────── Derived Values ─────────────────────────── */

  const filteredTasks = useMemo(() => {
    if (!activeProject) return [];
    return activeProject.tasks.filter(t => {
      const matchesSearch =
        !search ||
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.description.toLowerCase().includes(search.toLowerCase()) ||
        t.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
      const matchesAssignee = filterAssignee === 'all' || t.assignee.id === filterAssignee;
      const matchesPriority = filterPriority === 'all' || t.priority === filterPriority;
      const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
      return matchesSearch && matchesAssignee && matchesPriority && matchesStatus;
    });
  }, [activeProject, search, filterAssignee, filterPriority, filterStatus]);

  const overdueIds = useMemo(() => {
    const now = Date.now();
    return new Set(filteredTasks.filter(t => t.dueDate.getTime() < now && t.status !== 'done').map(t => t.id));
  }, [filteredTasks]);

  /* ─────────────────────────────── Rendering ────────────────────────────── */

  if (loading || !activeProject) {
    return (
      <div className="project-loading">
        <div className="loading-spinner"></div>
        <p>Loading project dashboard...</p>
      </div>
    );
  }

  return (
    <div className="project-manager-advanced">
      {/* Header */}
      <div className="project-header">
        <div className="header-left">
          <Link to="/" className="back-button-project">← Back to Home</Link>
          <div className="title-section">
            <h1>🚀 Project Command Center</h1>
            <div className="version-badge-project v15">Synoppy 1.5</div>
          </div>
        </div>

        <div className="header-controls">
          <div className="project-selector">
            <select value={activeProjectId || ''} onChange={(e) => handleProjectChange(e.target.value)}>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
          <div className="notifications">
            <span className="notification-icon">🔔</span>
            {notifications > 0 && <span className="notification-badge">{notifications}</span>}
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="view-tabs">
        {(['dashboard', 'kanban', 'gantt', 'team'] as const).map(view => (
          <button
            key={view}
            className={activeView === view ? 'active' : ''}
            onClick={() => setActiveView(view)}
          >
            {view === 'dashboard' && '📊 Dashboard'}
            {view === 'kanban' && '📋 Kanban'}
            {view === 'gantt' && '📈 Gantt'}
            {view === 'team' && '👥 Team'}
          </button>
        ))}
      </div>

      {/* Filters + Quick Add (visible in dashboard & kanban) */}
      {(activeView === 'dashboard' || activeView === 'kanban') && (
        <div className="toolbar">
          <div className="filters">
            <input
              className="input search"
              placeholder="Search title, description or tags…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className="input" value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
              <option value="all">All assignees</option>
              {activeProject.team.map(m => (
                <option value={m.id} key={m.id}>{m.name}</option>
              ))}
            </select>
            <select className="input" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value as any)}>
              <option value="all">All priorities</option>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
              <option value="all">All statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <form className="quick-add" onSubmit={addTask}>
            <input
              className="input"
              placeholder="Quick add task…"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <select className="input" value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}>
              <option value="unassigned">Assignee…</option>
              {activeProject.team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select className="input" value={newPriority} onChange={(e) => setNewPriority(e.target.value as any)}>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <button className="btn primary" type="submit">＋ Add</button>
          </form>
        </div>
      )}

      {/* DASHBOARD */}
      {activeView === 'dashboard' && (
        <div className="dashboard-section">
          <div className="project-overview">
            <div className="project-card">
              <div className="project-info">
                <h2>{activeProject.name}</h2>
                <p className="muted">{activeProject.description}</p>
                <div className="project-status">
                  <span className={`status-badge ${activeProject.status}`}>
                    {activeProject.status.replace('-', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="project-progress">
                <div className="progress-circle">
                  <svg viewBox="0 0 36 36">
                    <path
                      className="circle-bg"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="circle-progress"
                      strokeDasharray={`${activeProject.progress}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="progress-text">{activeProject.progress}%</div>
                </div>
                <div className="dates-row">
                  <span>Start: {activeProject.startDate.toLocaleDateString()}</span>
                  <span>End: {activeProject.endDate.toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">📋</div>
              <div className="metric-info">
                <div className="metric-value">{activeProject.tasks.length}</div>
                <div className="metric-label">Total Tasks</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">✅</div>
              <div className="metric-info">
                <div className="metric-value">
                  {activeProject.tasks.filter(t => t.status === 'done').length}
                </div>
                <div className="metric-label">Completed</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">💰</div>
              <div className="metric-info">
                <div className="metric-value">
                  {fmtMoney(activeProject.budget - activeProject.spent)}
                </div>
                <div className="metric-label">Remaining Budget</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">⏱️</div>
              <div className="metric-info">
                <div className="metric-value">
                  {activeProject.tasks.reduce((acc, task) => acc + task.actualHours, 0).toFixed(1)}h
                </div>
                <div className="metric-label">Hours Logged</div>
              </div>
            </div>
          </div>

          <div className="recent-tasks">
            <h3>Recent / Filtered Tasks</h3>
            <div className="tasks-list">
              {filteredTasks.slice(0, 8).map(task => (
                <div key={task.id} className={`task-card ${overdueIds.has(task.id) ? 'overdue' : ''}`}>
                  <div className="task-header">
                    <div className="task-title">{task.title}</div>
                    <div className="task-controls">
                      <button
                        className={`time-tracking ${timeTracking[task.id] ? 'active' : ''}`}
                        onClick={() => toggleTime(task.id)}
                        disabled={task.status === 'done'}
                        title="Start/Stop time tracking"
                      >
                        {timeTracking[task.id] ? '⏹️' : '▶️'}
                      </button>
                      <select
                        value={task.status}
                        onChange={(e) => handleTaskStatusChange(task.id, e.target.value as Task['status'])}
                        className="status-select"
                        title="Change status"
                      >
                        <option value="todo">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="review">Review</option>
                        <option value="done">Done</option>
                      </select>
                      <select
                        value={task.priority}
                        onChange={(e) => handleTaskPriorityChange(task.id, e.target.value as Task['priority'])}
                        className="priority-select"
                        title="Change priority"
                      >
                        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="task-meta">
                    <div className="task-assignee">
                      <span className="assignee-avatar">{task.assignee.avatar}</span>
                      {task.assignee.name}
                    </div>
                    <div
                      className="task-priority"
                      style={{ backgroundColor: getPriorityColor(task.priority) }}
                    >
                      {task.priority}
                    </div>
                    <div className="task-due muted">
                      Due {task.dueDate.toLocaleDateString()}
                    </div>
                  </div>

                  <div className="task-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${task.progress}%` }}></div>
                    </div>
                    <span className="progress-text">{task.progress}%</span>
                  </div>

                  <div className="task-footer">
                    <div className="task-tags">
                      {task.tags.map((tag, index) => (
                        <span key={index} className="task-tag">{tag}</span>
                      ))}
                    </div>
                    <div className="task-hours">
                      {task.actualHours.toFixed(1)}h / {task.estimatedHours}h
                    </div>
                  </div>
                </div>
              ))}
              {filteredTasks.length === 0 && (
                <div className="empty-state">No tasks match your filters.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TEAM */}
      {activeView === 'team' && (
        <div className="team-section">
          <div className="team-overview">
            <h3>Team Members</h3>
            <div className="team-grid">
              {activeProject.team.map(member => {
                const assignedCount = activeProject.tasks.filter(t => t.assignee.id === member.id).length;
                return (
                  <div key={member.id} className="team-member-card">
                    <div className="member-avatar-container">
                      <div className="member-avatar">{member.avatar}</div>
                      <div className={`status-indicator ${member.status}`}></div>
                    </div>
                    <div className="member-info">
                      <div className="member-name">{member.name}</div>
                      <div className="member-role">{member.role}</div>
                      <div className="member-tasks">{assignedCount} tasks assigned</div>
                    </div>
                    <div className="member-workload">
                      <div className="workload-bar">
                        <div
                          className="workload-fill"
                          style={{ width: `${Math.round(Math.random() * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* KANBAN (drag & drop) */}
      {activeView === 'kanban' && (
        <div className="kanban-section">
          <div className="kanban-board">
            {STATUSES.map((status: StatusUnion) => {
              const columnTasks = filteredTasks.filter(t => t.status === status);
              return (
                <div
                  key={status}
                  className="kanban-column"
                  onDragOver={onDragOver}
                  onDrop={() => onDropTo(status)}
                >
                  <div className="column-header">
                    <h4>{status.replace('-', ' ').toUpperCase()}</h4>
                    <span className="task-count">{columnTasks.length}</span>
                  </div>
                  <div className="column-tasks">
                    {columnTasks.map(task => (
                      <div
                        key={task.id}
                        className={`kanban-task ${overdueIds.has(task.id) ? 'overdue' : ''}`}
                        draggable
                        onDragStart={() => onDragStart(task.id)}
                        title="Drag to move"
                      >
                        <div className="task-title-row">
                          <div className="task-title">{task.title}</div>
                          <span
                            className="priority-dot"
                            style={{ backgroundColor: getPriorityColor(task.priority) }}
                            title={`Priority: ${task.priority}`}
                          />
                        </div>
                        <div className="task-assignee">
                          <span className="assignee-avatar">{task.assignee.avatar}</span>
                          {task.assignee.name}
                        </div>
                        <div className="task-mini-progress">
                          <div className="mini-bar">
                            <div className="mini-fill" style={{ width: `${task.progress}%` }} />
                          </div>
                          <span className="mini-text">{task.progress}%</span>
                        </div>
                      </div>
                    ))}
                    {columnTasks.length === 0 && (
                      <div className="kanban-empty muted">Drop tasks here</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* GANTT (simple SVG timeline) */}
      {activeView === 'gantt' && (
        <div className="gantt-section">
          <h3>Timeline</h3>
          <GanttView project={activeProject} tasks={filteredTasks} />
        </div>
      )}

      {/* Footer */}
      <div className="version-comparison-project">
        <Link to="?version=1.0" className="downgrade-link-project">
          Compare with Synoppy 1.0 Version →
        </Link>
      </div>

      <div className="features-showcase">
        <div className="feature">📋 Drag & drop Kanban</div>
        <div className="feature">⏱️ Per-task time tracking</div>
        <div className="feature">🔎 Filters & quick add</div>
        <div className="feature">📈 Gantt preview</div>
        <div className="feature">💰 Budget management</div>
        <div className="feature">🔄 Local persistence</div>
      </div>
    </div>
  );
};

/* ───────────────────────────── Gantt Subcomponent ───────────────────────── */

const ROW_H = 30;
const PAD_X = 16;

const GanttView: React.FC<{ project: Project; tasks: Task[] }> = ({ project, tasks }) => {
  const start = project.startDate.getTime();
  const end = project.endDate.getTime();
  const span = Math.max(1, end - start);
  const width = 900;

  // compute bars
  const rows = tasks
    .slice()
    .sort((a, b) => (a.startDate?.getTime() || start) - (b.startDate?.getTime() || start))
    .map((t, i) => {
      const s = (t.startDate?.getTime() || start);
      const e = t.dueDate.getTime();
      const x = PAD_X + ((s - start) / span) * (width - 2 * PAD_X);
      const w = Math.max(4, ((e - s) / span) * (width - 2 * PAD_X));
      return { t, y: i * ROW_H + 8, x, w };
    });

  return (
    <div className="gantt-wrapper">
      <svg className="gantt-svg" width={width} height={Math.max(120, rows.length * ROW_H + 20)}>
        {/* axis */}
        <line x1={PAD_X} y1={10} x2={width - PAD_X} y2={10} stroke="#e5e7eb" strokeWidth="1" />
        <text x={PAD_X} y={24} className="gantt-date">{project.startDate.toLocaleDateString()}</text>
        <text x={width - PAD_X - 100} y={24} className="gantt-date">{project.endDate.toLocaleDateString()}</text>

        {rows.map(({ t, y, x, w }, idx) => (
          <g key={t.id} transform={`translate(0, ${y})`}>
            <rect x={x} y={8} width={w} height={12} rx={4} fill={getPriorityColor(t.priority)} opacity="0.85" />
            <text x={x + 6} y={18} className="gantt-task-label">
              {t.title} • {t.assignee.name}
            </text>
          </g>
        ))}
      </svg>
      {rows.length === 0 && <div className="empty-state">No tasks to plot.</div>}
    </div>
  );
};

export default ProjectManagerAdvanced;
